import { Spinner } from "@/components/ui/spinner";
import { useState } from "react";
import { ExpenseCategory, MismatchStatus, dayjs } from "@boo/shared";
import type { Transaction, TransactionMismatchDto } from "@boo/shared";
import { useReconciliationStore } from "../store/useReconciliationStore";
import { CATEGORY_LABELS } from "../types";
import { formatSource } from "../lib/formatSource";
import AmountInput from "@/components/AmountInput";

const EXPENSE_CATEGORIES: ExpenseCategory[] = [
  ExpenseCategory.FOOD,
  ExpenseCategory.TRANSPORT,
  ExpenseCategory.SHOPPING,
  ExpenseCategory.BILLS,
  ExpenseCategory.ENTERTAINMENT,
  ExpenseCategory.HEALTH,
  ExpenseCategory.HOUSING,
  ExpenseCategory.FINANCIAL,
  ExpenseCategory.INVESTMENT,
  ExpenseCategory.OTHER,
];

const inputClass =
  "w-full rounded-[8px] border-[0.5px] border-border bg-[var(--surface-muted)] px-3 py-2 text-[13px] text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-border";

/** Fields shown side-by-side, in display order. */
const COMPARABLE_FIELDS = [
  "title",
  "amount",
  "date",
  "category",
  "note",
] as const;
type ComparableField = (typeof COMPARABLE_FIELDS)[number];

const FIELD_LABELS: Record<ComparableField, string> = {
  title: "Title",
  amount: "Amount",
  date: "Date",
  category: "Category",
  note: "Note",
};

function formatValue(field: ComparableField, value: unknown): string {
  if (value === null || value === undefined || value === "") return "—";
  if (field === "amount") return `฿${Number(value).toLocaleString()}`;
  if (field === "date") return dayjs(value as string).format("YYYY-MM-DD");
  if (field === "category") {
    const key = value as keyof typeof CATEGORY_LABELS;
    return CATEGORY_LABELS[key] ?? String(value);
  }
  return String(value);
}

/** A field differs when the PDF and current snapshots are not equal. */
function fieldDiffers(
  field: ComparableField,
  pdf: Partial<Transaction>,
  current: Partial<Transaction>,
): boolean {
  if (field === "date") {
    return (
      dayjs(pdf.date).format("YYYY-MM-DD") !==
      dayjs(current.date).format("YYYY-MM-DD")
    );
  }
  return (pdf[field] ?? "") !== (current[field] ?? "");
}

export default function MismatchCard({
  mismatch,
}: {
  mismatch: TransactionMismatchDto;
}) {
  const resolveMismatch = useReconciliationStore((s) => s.resolveMismatch);
  const [editing, setEditing] = useState(false);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { pdfData, currentData } = mismatch;
  // A possible duplicate compares two stored rows from different sources, so
  // every field is shown and the columns are named after each row's source.
  const isDuplicate = !!mismatch.duplicateTransactionId;
  const pdfLabel = isDuplicate
    ? formatSource(pdfData.source ?? "")
    : "PDF version";
  const currentLabel = isDuplicate
    ? formatSource(currentData.source ?? "")
    : "My version";
  const shownFields = isDuplicate
    ? COMPARABLE_FIELDS
    : COMPARABLE_FIELDS.filter((f) => fieldDiffers(f, pdfData, currentData));

  const resolve = async (
    action: MismatchStatus,
    editedData?: Partial<Transaction>,
  ) => {
    setPending(true);
    setError(null);
    try {
      await resolveMismatch(mismatch.id, action, editedData);
    } catch {
      setError("Could not resolve. Please try again.");
      setPending(false);
    }
  };

  return (
    <div className="rounded-[12px] border-[0.5px] border-border bg-card p-4">
      {/* Side-by-side diff */}
      <div className="grid grid-cols-[80px_1fr_1fr] gap-x-3 gap-y-2 text-[13px]">
        <div />
        <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
          {pdfLabel}
        </div>
        <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
          {currentLabel}
        </div>

        {shownFields.map((field) => (
          <div key={field} className="contents">
            <div className="text-[12px] text-muted-foreground self-center">
              {FIELD_LABELS[field]}
            </div>
            <div className="text-foreground [font-variant-numeric:tabular-nums]">
              {formatValue(field, pdfData[field])}
            </div>
            <div className="text-foreground [font-variant-numeric:tabular-nums]">
              {formatValue(field, currentData[field])}
            </div>
          </div>
        ))}
      </div>

      {error && <p className="mt-3 text-[12px] text-destructive">{error}</p>}

      {editing ? (
        <EditForm
          current={currentData}
          pending={pending}
          onCancel={() => setEditing(false)}
          onSave={(editedData) =>
            resolve(MismatchStatus.ResolvedEditManual, editedData)
          }
        />
      ) : (
        <div className="mt-4 flex flex-wrap items-center gap-2">
          <button
            type="button"
            disabled={pending}
            onClick={() => resolve(MismatchStatus.ResolvedAcceptPdf)}
            className="inline-flex items-center gap-1.5 text-[13px] font-medium px-3.5 py-1.5 rounded-[10px] bg-primary text-primary-foreground hover:opacity-90 transition-opacity disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
          >
            {pending && <Spinner className="size-3.5" />}
            {isDuplicate ? `Same — keep ${pdfLabel}` : "Accept PDF version"}
          </button>
          <button
            type="button"
            disabled={pending}
            onClick={() => resolve(MismatchStatus.ResolvedKeepMine)}
            className="text-[13px] font-medium px-3.5 py-1.5 rounded-[10px] bg-transparent text-foreground border border-border hover:bg-[var(--surface-muted)] transition-colors disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
          >
            {isDuplicate ? `Same — keep ${currentLabel}` : "Keep my version"}
          </button>
          {isDuplicate && (
            <button
              type="button"
              disabled={pending}
              onClick={() => resolve(MismatchStatus.ResolvedKeepBoth)}
              className="text-[13px] font-medium px-3.5 py-1.5 rounded-[10px] bg-transparent text-foreground border border-border hover:bg-[var(--surface-muted)] transition-colors disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
            >
              Different — keep both
            </button>
          )}
          <button
            type="button"
            disabled={pending}
            onClick={() => setEditing(true)}
            className="text-[13px] font-medium px-3.5 py-1.5 rounded-[10px] bg-transparent text-muted-foreground border border-border hover:bg-[var(--surface-muted)] transition-colors disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
          >
            Edit manually
          </button>
        </div>
      )}
    </div>
  );
}

function resolveInitialCategory(
  value: Partial<Transaction>["category"],
): ExpenseCategory {
  return EXPENSE_CATEGORIES.includes(value as ExpenseCategory)
    ? (value as ExpenseCategory)
    : ExpenseCategory.OTHER;
}

interface EditFormProps {
  current: Partial<Transaction>;
  pending: boolean;
  onCancel: () => void;
  onSave: (editedData: Partial<Transaction>) => void;
}

function EditForm({ current, pending, onCancel, onSave }: EditFormProps) {
  const [amount, setAmount] = useState(
    current.amount !== undefined ? String(current.amount) : "",
  );
  const [title, setTitle] = useState(current.title ?? "");
  const [date, setDate] = useState(
    current.date ? dayjs(current.date).format("YYYY-MM-DD") : "",
  );
  const [category, setCategory] = useState<ExpenseCategory>(
    resolveInitialCategory(current.category),
  );
  const [note, setNote] = useState(current.note ?? "");
  const [formError, setFormError] = useState<string | null>(null);

  const handleSave = () => {
    const amountNum = parseFloat(amount);
    if (isNaN(amountNum) || amountNum <= 0) {
      setFormError("Enter a valid amount.");
      return;
    }
    if (!title.trim()) {
      setFormError("Title is required.");
      return;
    }
    setFormError(null);
    onSave({
      amount: amountNum,
      title: title.trim(),
      date,
      category,
      note: note.trim(),
    });
  };

  return (
    <div className="mt-4 flex flex-col gap-3 border-t border-border pt-4">
      <div className="grid grid-cols-2 gap-3">
        <label className="flex flex-col gap-1.5">
          <span className="text-[12px] text-muted-foreground font-medium">
            Amount (฿)
          </span>
          <AmountInput
            className={`${inputClass} [font-variant-numeric:tabular-nums]`}
            value={amount}
            onChange={setAmount}
          />
        </label>
        <label className="flex flex-col gap-1.5">
          <span className="text-[12px] text-muted-foreground font-medium">
            Date
          </span>
          <input
            type="date"
            className={`${inputClass} [font-variant-numeric:tabular-nums]`}
            value={date}
            onChange={(e) => setDate(e.target.value)}
          />
        </label>
      </div>

      <label className="flex flex-col gap-1.5">
        <span className="text-[12px] text-muted-foreground font-medium">
          Title
        </span>
        <input
          className={inputClass}
          maxLength={200}
          value={title}
          onChange={(e) => setTitle(e.target.value)}
        />
      </label>

      <label className="flex flex-col gap-1.5">
        <span className="text-[12px] text-muted-foreground font-medium">
          Category
        </span>
        <select
          className={inputClass}
          value={category}
          onChange={(e) => setCategory(e.target.value as ExpenseCategory)}
        >
          {EXPENSE_CATEGORIES.map((cat) => (
            <option key={cat} value={cat}>
              {CATEGORY_LABELS[cat]}
            </option>
          ))}
        </select>
      </label>

      <label className="flex flex-col gap-1.5">
        <span className="text-[12px] text-muted-foreground font-medium">
          Note
        </span>
        <textarea
          className={`${inputClass} resize-none`}
          rows={2}
          maxLength={500}
          value={note}
          onChange={(e) => setNote(e.target.value)}
        />
      </label>

      {formError && (
        <span className="text-[12px] text-destructive">{formError}</span>
      )}

      <div className="flex items-center justify-end gap-2">
        <button
          type="button"
          onClick={onCancel}
          disabled={pending}
          className="text-[13px] font-medium px-4 py-2 rounded-[10px] bg-transparent text-[var(--text-secondary)] border border-border hover:bg-[var(--surface-muted)] transition-colors disabled:opacity-40 cursor-pointer"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={handleSave}
          disabled={pending}
          className="inline-flex items-center gap-1.5 text-[13px] font-medium px-4 py-2 rounded-[10px] bg-primary text-primary-foreground hover:opacity-90 transition-opacity disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
        >
          {pending && <Spinner className="size-3.5" />}
          Save
        </button>
      </div>
    </div>
  );
}
